import { DialogTitle } from "@radix-ui/react-dialog";
import { DialogHeader } from "../../utils/components/ui/dialog";
import { Button } from "../../utils/components/ui/button";
import { ErrorWraper } from "../common/ErrorWrapper";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { AddUserSchema, AddUserSchemaT } from "../../types/schema/userScheme";
import {
  useAddUser,
  useGetAUser,
  useUpdateUser,
} from "../../service/userService";
import { useQueryClient } from "@tanstack/react-query";
import DropDownComponent from "../common/DropDownComponent";
import useUserRequestStore from "../../store/useUserStore";
import { useEffect, useState } from "react";
import Loading from "../common/LoaderCommon";
import { Eye, EyeOff } from "lucide-react";

interface Props {
  setOpen: (open: boolean) => void;
}

function AddNewUser({ setOpen }: Props) {
  const queryClient = useQueryClient();
  const { currentUser, setCurrentUser } = useUserRequestStore();
  const [showPassword, setShowPassword] = useState(false);

  const { mutate: addUser } = useAddUser();
  const { mutate: updateUser } = useUpdateUser();

  // Only fetch user data if we have a currentUser with email for editing
  const { data: user, isLoading: isUserLoading } = useGetAUser({
    email: currentUser?.email || "",
  });

  console.log(currentUser, "currentUser from add new");
  console.log(user, "fetched user data");

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm<AddUserSchemaT>({
    resolver: zodResolver(AddUserSchema),
    defaultValues: {
      username: "",
      phoneNumber: "",
      email: "",
      password: "",
      role: "",
      address: "",
    },
  });

  // Effect to populate form when editing existing user
  useEffect(() => {
    if (currentUser) {
      // If we have currentUser data, use it directly
      console.log("Populating form with currentUser data:", currentUser);
      setValue("username", currentUser.username || "");
      setValue("phoneNumber", currentUser.phoneNumber || "");
      setValue("email", currentUser.email || "");
      setValue("password", currentUser.password || "");
      setValue("role", currentUser.role || "");
      setValue("address", currentUser.address || "");
    } else {
      // Reset form for new user
      reset({
        username: "",
        phoneNumber: "",
        email: "",
        password: "",
        role: "",
        address: "",
      });
    }
  }, [currentUser, setValue, reset]);

  // Additional effect to handle fetched user data (if needed)
  useEffect(() => {
    if (user && currentUser?.email) {
      console.log("Populating form with fetched user data:", user);
      setValue("username", user.username || "");
      setValue("phoneNumber", user.phoneNumber || "");
      setValue("email", user.email || "");
      setValue("password", user.password || "");
      setValue("role", user.role || "");
      setValue("address", user.address || "");
    }
  }, [user, currentUser?.email, setValue]);

  const handleAddUser = (data: AddUserSchemaT) => {
    const postData = currentUser?.id ? { ...data, id: currentUser.id } : data;

    if (currentUser?.id) {
      updateUser(postData, {
        onSuccess: () => {
          toast.success("User updated successfully!");
          setCurrentUser(null);
          setOpen(false);
          queryClient.invalidateQueries({
            queryKey: ["useInfiniteGetAllUsers"],
          });
        },
        onError: (error: Error) => {
          toast.error(`Error updating user: ${error.message}`);
          console.error("Error updating user", error);
        },
      });
    } else {
      addUser(postData, {
        onSuccess: () => {
          toast.success("User added successfully!");
          setOpen(false);
          queryClient.invalidateQueries({
            queryKey: ["useInfiniteGetAllUsers"],
          });
        },
        onError: (error: Error) => {
          toast.error(`Error adding user: ${error.message}`);
          console.error("Error adding user", error);
        },
      });
    }
  };

  const handleCancel = () => {
    setOpen(false);
    setCurrentUser(null); // Clear current user when canceling
    reset();
  };

  // Show loading state if fetching user data
  if (isUserLoading && currentUser?.email) {
    return (
      <div className="space-y-4">
        <DialogHeader>
          <DialogTitle className="text-[#1A5EF9] h-[20vh] text-xl font-semibold">
            <Loading />
          </DialogTitle>
        </DialogHeader>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A5EF9]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle className="text-[#1A5EF9] text-xl font-semibold">
          {currentUser ? "Edit User" : "Add New User"}
        </DialogTitle>
      </DialogHeader>

      <form onSubmit={handleSubmit(handleAddUser)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[#1A5EF9] text-sm font-medium mb-1 block">
              Username
            </label>
            <ErrorWraper message={errors.username?.message}>
              <input
                type="text"
                className="w-full bg-[#F3F4F3] rounded border border-[#BAC4C0] h-10 px-3"
                placeholder="Username"
                {...register("username")}
              />
            </ErrorWraper>
          </div>
          <div>
            <label className="text-[#1A5EF9] text-sm font-medium mb-1 block">
              Phone Number
            </label>
            <ErrorWraper message={errors.phoneNumber?.message}>
              <input
                type="text"
                className="w-full bg-[#F3F4F3] rounded border border-[#BAC4C0] h-10 px-3"
                placeholder="Phone Number"
                {...register("phoneNumber")}
              />
            </ErrorWraper>
          </div>
          <div>
            <label className="text-[#1A5EF9] text-sm font-medium mb-1 block">
              Email
            </label>
            <ErrorWraper message={errors.email?.message}>
              <input
                type="email"
                className="w-full bg-[#F3F4F3] rounded border border-[#BAC4C0] h-10 px-3"
                placeholder="Email"
                {...register("email")}
              />
            </ErrorWraper>
          </div>
          <div>
            <label className="text-[#1A5EF9] text-sm font-medium mb-1 block">
              Password
            </label>
            <ErrorWraper message={errors.password?.message}>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full bg-[#F3F4F3] rounded border border-[#BAC4C0] h-10 px-3 pr-10"
                  placeholder="Password"
                  {...register("password")}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </ErrorWraper>
          </div>
          <div>
            <label className="text-[#1A5EF9] text-sm font-medium mb-1 block">
              Role
            </label>
            <ErrorWraper message={errors.role?.message}>
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <DropDownComponent
                    options={[
                      { id: "USER", name: "USER" },
                      { id: "ADMIN", name: "ADMIN" },
                      { id: "MANAGER", name: "MANAGER" },
                    ]}
                    placeholder="Select Role"
                    className="w-full bg-[#F3F4F3] border border-[#BAC4C0] rounded h-10 px-3"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </ErrorWraper>
          </div>
          <div>
            <label className="text-[#1A5EF9] text-sm font-medium mb-1 block">
              Address
            </label>
            <ErrorWraper message={errors.address?.message}>
              <input
                type="text"
                className="w-full bg-[#F3F4F3] rounded border border-[#BAC4C0] h-10 px-3"
                placeholder="Address"
                {...register("address")}
              />
            </ErrorWraper>
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            className="border-[#BAC4C0] text-[#84928C] hover:bg-gray-50 px-6 py-2"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-[#1A5EF9] hover:bg-[#1450D1] text-white px-6 py-2"
          >
            {currentUser ? "Update User" : "Save User"}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default AddNewUser;
